import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import api from '@/services/api';
import { useUserProfile } from '@/context/UserProfileContext';
import { getActivePortal, getUserData } from '@/utils/auth';

const ComplianceContext = createContext();

const parseEmailList = (raw) => {
    const list = String(raw || '')
        .toLowerCase()
        .split(/[\n,;\s]+/)
        .map(e => e.trim())
        .filter(Boolean);
    const set = new Set(list);
    set.add('saleejkuruniyan@budlee.ai');
    return Array.from(set);
};

export const ComplianceProvider = ({ children }) => {
    const { profile } = useUserProfile();
    const [rawComplianceData, setRawComplianceData] = useState({
        show_subscriptions_webapp: true,
        show_subscriptions_ios: true,
        show_subscriptions_android: true,
        compliance_review_emails: 'saleejkuruniyan@budlee.ai'
    });
    const [loading, setLoading] = useState(true);

    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();

    useEffect(() => {
        const fetchCompliance = async () => {
            try {
                const response = await api.get('superadmin/app-version/current/');
                if (response.data) {
                    setRawComplianceData({
                        show_subscriptions_webapp: response.data.show_subscriptions_webapp !== undefined ? response.data.show_subscriptions_webapp : true,
                        show_subscriptions_ios: response.data.show_subscriptions_ios !== undefined ? response.data.show_subscriptions_ios : true,
                        show_subscriptions_android: response.data.show_subscriptions_android !== undefined ? response.data.show_subscriptions_android : true,
                        compliance_review_emails: response.data.compliance_review_emails || 'saleejkuruniyan@budlee.ai'
                    });
                }
            } catch (error) {
                console.error('[Compliance] Failed to fetch settings:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCompliance();
    }, []);

    // Resolve current user's email across context and persistent auth storage
    const currentUserEmail = useMemo(() => {
        const portal = getActivePortal() || 'student';
        const storedUser = getUserData(portal);
        const email = profile?.email || profile?.username || storedUser?.email || storedUser?.username || '';
        return String(email).trim().toLowerCase();
    }, [profile]);

    const reviewEmails = useMemo(() => {
        return parseEmailList(rawComplianceData.compliance_review_emails);
    }, [rawComplianceData.compliance_review_emails]);

    const isReviewAccount = Boolean(
        currentUserEmail && reviewEmails.includes(currentUserEmail)
    );

    // Check platform toggle setting
    let platformSubscriptionsAllowed = true;
    if (!isNative) {
        platformSubscriptionsAllowed = rawComplianceData.show_subscriptions_webapp;
    } else if (platform === 'ios') {
        platformSubscriptionsAllowed = rawComplianceData.show_subscriptions_ios;
    } else if (platform === 'android') {
        platformSubscriptionsAllowed = rawComplianceData.show_subscriptions_android;
    }

    // Determine compliance visibility:
    // 1. Review account on mobile (iOS/Android native apps): ALWAYS hidden by default (no toggles needed).
    // 2. Platform toggle disabled: Acts as a global kill-switch, hiding subscriptions, tuitions, and courses for ALL users on that platform.
    // 3. Review account on web: Full public access unless show_subscriptions_webapp is explicitly disabled.
    const isReviewOnMobile = isReviewAccount && isNative;
    const isPlatformSwitchDisabled = !platformSubscriptionsAllowed;

    const compliance_mode_active = isReviewOnMobile || isPlatformSwitchDisabled;

    const show_subscriptions = !compliance_mode_active;
    const hide_tuition = compliance_mode_active;
    const hide_courses = compliance_mode_active;

    const current_platform = platform === 'ios' ? 'ios' : platform === 'android' ? 'android' : 'webapp';

    const value = {
        show_subscriptions,
        hide_tuition,
        hide_courses,
        isReviewAccount,
        compliance_mode_active,
        show_promotions: true,
        is_ios: platform === 'ios',
        is_android: platform === 'android',
        is_webapp: !isNative,
        current_platform,
        loading,
        compliance_loading: loading
    };

    return (
        <ComplianceContext.Provider value={value}>
            {children}
        </ComplianceContext.Provider>
    );
};

export const useCompliance = () => {
    const context = useContext(ComplianceContext);
    if (!context) {
        throw new Error('useCompliance must be used within a ComplianceProvider');
    }
    return context;
};
